import { CircleAlert } from 'lucide-react';
import React, { useId } from 'react';
import {
  Input,
  Select,
  Textarea,
  Label,
  controlClasses,
  fieldErrorClasses,
  fieldHelpClasses,
  fieldSlotClasses,
} from './primitives/input';
import cn from './primitives/cn';

/**
 * One labelled form control.
 *
 * The API has not changed: `label`, `as`, `id`, `help`, `error`, `required`, `optional`,
 * `labelCols`, `controlCols`, `after`, `className`, `labelClassName`. 27 call sites keep
 * working untouched. Underneath, the control is now the shadcn-styled input, select or
 * textarea from `primitives/input`, and the label is Radix's.
 *
 * **`as="select"` is still a native `<select>`.** Radix Select takes `<Select.Item>`
 * children and its own controlled-value contract, so adopting it would mean rewriting the
 * `<option>` lists and the change handler at every call site. That is a rewrite, not a
 * restyle, and it is out of scope here. The swap is a change to `primitives/input`.
 *
 * Three fixes carried over from the Bootstrap version, all bugs rather than style:
 *
 *   - **Labels get a real `htmlFor`.** Thirteen hand-written labels had none and nine
 *     controls had no `id`. `useId` generates one when the caller does not supply it.
 *   - **Help text is actually visible.** `main.css:1130` hides every `.help-block` and
 *     only `main.css:1151` brings it back, for `.has-error` and `.help-block.always-on`.
 *     A plain hint therefore rendered nothing at all. `help` writes `always-on`.
 *   - **Errors are announced.** `role="alert"` on the message.
 *
 * `labelCols`/`controlCols` are the horizontal shape, taken as column numbers so there is
 * one spelling of it. They emit the grid's `col-sm-*` classes, which main.css defines now
 * that Bootstrap is gone (all twelve, because the numbers come from props).
 *
 * The legacy `form-group` / `control-label` / `form-control` / `help-block` classes are
 * still emitted as hooks. `.form-group.input-short input` and friends size a control from
 * outside it, and Angular templates share the same stylesheet.
 */
const CONTROLS = { input: Input, select: Select, textarea: Textarea };

const Field = ({
  label,
  as = 'input',
  id,
  help,
  error,
  required = false,
  optional = false,
  labelCols,
  controlCols,
  after,
  className,
  labelClassName,
  children,
  ...rest
}) => {
  const generatedId = useId();
  const controlId = id || generatedId;
  const Control = CONTROLS[as] || Input;
  const describedBy = error || help ? `${controlId}-help` : undefined;

  const control = (
    <Control
      id={controlId}
      className="form-control"
      required={required}
      aria-invalid={error ? 'true' : undefined}
      aria-describedby={describedBy}
      {...rest}
    >
      {/* A select's children are its options, so they belong inside the control. Every
          other control is void or takes its value as a prop. */}
      {as === 'select' ? children : undefined}
    </Control>
  );

  // `children` means one of two different things, so the select case is settled first:
  // options for a select, or a caller-supplied control replacing the input entirely.
  const body = as === 'select' ? control : children || control;

  // Help text and the error share one slot under the control, as the design's fields do.
  const message = (
    <>
      {error && (
        <span
          className={cn('help-block', fieldErrorClasses, fieldSlotClasses)}
          id={`${controlId}-help`}
          role="alert"
        >
          <CircleAlert className="tw-flex-none tw-mt-px" size={14} aria-hidden="true" />
          <span>{error}</span>
        </span>
      )}
      {!error && help && (
        <span
          className={cn('help-block always-on', fieldHelpClasses, fieldSlotClasses)}
          id={`${controlId}-help`}
        >
          {help}
        </span>
      )}
    </>
  );

  return (
    <div className={cn('form-group', 'tw-mb-4', className, error && 'has-error')}>
      {label && (
        <Label
          htmlFor={controlId}
          className={cn(
            labelCols && `col-sm-${labelCols}`,
            'control-label',
            labelCols && 'tw-pt-control-y',
            labelClassName
          )}
        >
          {label}
          {/* The red asterisk every required field carries (FieldWrapper's RequiredMark),
              which a grey Save's tooltip points at (SURF-4889). `required` used to reach
              only the input's attribute, so these fields were required unmarked. */}
          {required && <span className="required-annotation tw-text-error-text">*</span>}
          {optional && <span className="tw-font-normal tw-text-muted"> (Optional)</span>}
        </Label>
      )}
      {controlCols ? (
        // In a horizontal group the message belongs inside the control's column, or it
        // wraps onto its own full-width row underneath the label.
        <div className={`col-sm-${controlCols}`}>
          {body}
          {after}
          {message}
        </div>
      ) : (
        <>
          {body}
          {after}
          {message}
        </>
      )}
    </div>
  );
};

export { controlClasses };
export default Field;
